// A small outline glyph sitting inside a text field, so a row of otherwise
// identical inputs can be read at a glance instead of by remembering the order.
//
// Matches the outline icons already used across the app: 24x24, no fill,
// currentColor stroke. Place inside a wrapper with `group relative` and give the
// field enough left padding to clear the glyph — `pl-7` at sm, `pl-8` at md.

const PATHS = {
  // A person — the student's name.
  name: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
  // An envelope — the parent's email.
  email: 'M3 8l7.9 5.26a2 2 0 002.2 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
  // Ragged lines of text — free-form notes.
  notes: 'M4 7h16M4 12h11M4 17h7',
  // Camera-in-a-square — an Instagram post.
  instagram:
    'M7.5 3h9A4.5 4.5 0 0121 7.5v9a4.5 4.5 0 01-4.5 4.5h-9A4.5 4.5 0 013 16.5v-9A4.5 4.5 0 017.5 3z M12 8.5a3.5 3.5 0 110 7 3.5 3.5 0 010-7z M16.9 7.1h.01',
};

const SIZES = {
  sm: 'w-3.5 h-3.5',
  md: 'w-4 h-4',
};

// Offset that centres the glyph on the *first* line of a multi-line field:
// border (1px) + py-1 (4px) + half the line box, less half the glyph.
// sm is text-xs/16px line with a 14px glyph; md is text-sm/20px with a 16px one.
const TOP_ALIGNED = {
  sm: 'top-1.5',
  md: 'top-2',
};

/** The bare glyph, for labelling read-only text rather than a field. */
export const Glyph = ({ name, size = 'sm', className = '' }) => (
  <svg
    aria-hidden="true"
    className={`${SIZES[size]} ${className}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={PATHS[name]} />
  </svg>
);

/**
 * `alignTop` pins the glyph to the first line rather than centring it, which is
 * what a multi-line field needs — a centred icon drifts as the box grows.
 */
const FieldIcon = ({ name, size = 'sm', alignTop = false }) => (
  <span
    aria-hidden="true"
    className={`pointer-events-none absolute left-2 text-gray-400 transition-colors group-focus-within:text-purple-500 ${
      alignTop ? TOP_ALIGNED[size] : 'top-1/2 -translate-y-1/2'
    }`}
  >
    <svg className={SIZES[size]} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={PATHS[name]} />
    </svg>
  </span>
);

export default FieldIcon;
